/**
 * Form1098Tab — Visual replica of IRS Form 1098 (Mortgage Interest Statement)
 * Copy B — For Payer/Borrower
 *
 * Renders the form layout with data from servicing_loans + encompass_loans.
 */

import { useMemo } from 'react';
import type { ServicingLoan, Form1098Data } from '@/types/servicing';
import { toNumber } from '@/types/servicing';
import { formatCurrency } from '@/components/banker-recon/types';
import { formatDateDisplay } from '@/utils/dateHelpers';

// ---------------------------------------------------------------------------
// Props
// ---------------------------------------------------------------------------

interface Form1098TabProps {
  loan: ServicingLoan;
  form1098Data: Form1098Data | null;
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/** Format date for form box values — returns empty string instead of '--' for blank boxes. */
function formatDateForBox(date: string | null | undefined): string {
  const result = formatDateDisplay(date);
  return result === '--' ? '' : result;
}

function formatAmount(val: number): string {
  if (val === 0) {
return '$ 0.00';
}
  return `$ ${val.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function Form1098Tab({ loan, form1098Data }: Form1098TabProps) {
  const computed = useMemo(() => {
    const prepaidInterest = toNumber(loan.prepaid_interest);
    const totalInterestCollected = toNumber(loan.total_interest_collected);
    const loanAmount = toNumber(loan.loan_amount);
    const totalPrincipalCollected = toNumber(loan.total_principal_collected);
    const paymentCount = loan.payment_count ?? 0;
    const miPerMonth = toNumber(form1098Data?.proposed_mi_payment);

    // Box 1: Mortgage interest received = prepaid at closing + collected during servicing
    const box1 = Math.round((prepaidInterest + totalInterestCollected) * 100) / 100;

    // Box 2: Outstanding mortgage principal (remaining balance)
    const box2 = Math.round((loanAmount - totalPrincipalCollected) * 100) / 100;

    // Box 3: Mortgage origination date
    const box3 = formatDateForBox(form1098Data?.closing_date ?? loan.closing_date);

    // Box 4: Refund of overpaid interest
    const box4 = 0;

    // Box 5: Mortgage insurance premiums
    const box5 = Math.round(miPerMonth * paymentCount * 100) / 100;

    // Box 6: Points paid on purchase
    const box6 = 0;

    // Box 7: Address of property is same as payer's mailing address
    const propertyAddr = (form1098Data?.property_address ?? '').toLowerCase().trim();
    const mailingAddr = (form1098Data?.borrower_mailing_street ?? '').toLowerCase().trim();
    const box7 = !!(propertyAddr && mailingAddr && propertyAddr === mailingAddr);

    // Box 8: Property address
    const box8Parts = [
      form1098Data?.property_address,
      [form1098Data?.subject_property_city, form1098Data?.subject_property_state].filter(Boolean).join(', '),
      form1098Data?.property_zip
    ].filter(Boolean);
    const box8 = box8Parts.join(', ');

    // Box 11: Mortgage acquisition date
    const box11 = formatDateForBox(loan.investor_purchase_date);

    // Borrower mailing address
    const borrowerAddress = form1098Data?.borrower_mailing_street ?? '';
    const borrowerCityStateZip = [
      form1098Data?.borrower_mailing_city,
      form1098Data?.borrower_mailing_state
    ].filter(Boolean).join(', ') + (form1098Data?.borrower_mailing_zip ? ` ${form1098Data.borrower_mailing_zip}` : '');

    const taxYear = String(new Date().getFullYear());

    return { box1, box2, box3, box4, box5, box6, box7, box8, box11, borrowerAddress, borrowerCityStateZip, taxYear };
  }, [loan, form1098Data]);

  return (
    <div className="mt-6 space-y-4">
      {/* Form container */}
      <div className="border-2 border-black bg-white text-black text-[11px] leading-tight font-['Courier_New',monospace] max-w-[700px] mx-auto">

        {/* ─── Top header row ─── */}
        <div className="flex border-b-2 border-black">
          {/* CORRECTED checkbox */}
          <div className="w-[120px] p-1.5 border-r border-black text-[9px]">
            <div className="flex items-center gap-1">
              <span className="inline-block w-3 h-3 border border-black" />
              <span>CORRECTED (if checked)</span>
            </div>
          </div>
          {/* Caution + OMB */}
          <div className="flex-1 px-2 py-1">
            <div className="flex justify-between items-start">
              <span className="text-[9px] italic">
                Caution: The amount shown may not be fully deductible by you. Limits based on the debt may apply. See instructions.
              </span>
              <span className="text-[9px] whitespace-nowrap ml-2">OMB No. 1545-1380</span>
            </div>
          </div>
        </div>

        {/* ─── Main form body ─── */}
        <div className="flex">
          {/* ═══ LEFT COLUMN ═══ */}
          <div className="w-[280px] border-r-2 border-black">

            {/* Lender info */}
            <div className="p-1.5 border-b border-black min-h-[60px]">
              <div className="text-[8px] text-gray-500 uppercase mb-0.5">
                Recipient&apos;s/Lender&apos;s name, street address, city or town, state or province, country, ZIP
              </div>
              <div className="font-bold text-[12px]">{form1098Data?.lender_name || 'Lendwise Mortgage Inc.'}</div>
              <div>{form1098Data?.lender_address || ''}</div>
              <div>{form1098Data?.lender_city_state_zip || ''}</div>
              <div>{form1098Data?.lender_phone || ''}</div>
            </div>

            {/* TIN row */}
            <div className="flex border-b border-black">
              <div className="flex-1 p-1.5 border-r border-black">
                <div className="text-[8px] text-gray-500 uppercase mb-0.5">
                  Recipient&apos;s/Lender&apos;s TIN
                </div>
                <div className="font-bold">{form1098Data?.lender_tin || ''}</div>
              </div>
              <div className="flex-1 p-1.5">
                <div className="text-[8px] text-gray-500 uppercase mb-0.5">
                  Payer&apos;s/Borrower&apos;s TIN
                </div>
                <div className="font-bold">
                  {form1098Data?.borrower_ssn_last4 ? `***-**-${form1098Data.borrower_ssn_last4}` : '***-**-****'}
                </div>
              </div>
            </div>

            {/* Borrower name */}
            <div className="p-1.5 border-b border-black">
              <div className="text-[8px] text-gray-500 uppercase mb-0.5">
                Payer&apos;s/Borrower&apos;s name
              </div>
              <div className="font-bold">{loan.borrower_name || '--'}</div>
            </div>

            {/* Borrower address */}
            <div className="p-1.5 border-b border-black min-h-[44px]">
              <div className="text-[8px] text-gray-500 uppercase mb-0.5">
                Street address (including apt. no.)
              </div>
              <div>{computed.borrowerAddress || '--'}</div>
            </div>

            <div className="p-1.5 border-b border-black">
              <div className="text-[8px] text-gray-500 uppercase mb-0.5">
                City or town, state or province, country, and ZIP or foreign postal code
              </div>
              <div>{computed.borrowerCityStateZip || '--'}</div>
            </div>

            {/* Account number */}
            <div className="p-1.5">
              <div className="text-[8px] text-gray-500 uppercase mb-0.5">
                Account number (see instructions)
              </div>
              <div className="font-bold text-[12px]">{loan.loan_number}</div>
            </div>
          </div>

          {/* ═══ RIGHT COLUMN ═══ */}
          <div className="flex-1 flex flex-col">

            {/* Form title */}
            <div className="p-1.5 border-b border-black text-center">
              <div className="text-[9px]">Form <span className="font-bold text-[14px]">1098</span></div>
              <div className="text-[9px] font-bold">Mortgage Interest Statement</div>
              <div className="text-[9px]">Copy B — For Payer/Borrower</div>
              <div className="text-[9px] mt-0.5">
                For calendar year <span className="font-bold underline">{computed.taxYear}</span>
              </div>
            </div>

            {/* Box 1 */}
            <FormBox
              number="1"
              label="Mortgage interest received from payer(s)/borrower(s)"
              value={formatAmount(computed.box1)}
            />

            {/* Box 2 */}
            <FormBox
              number="2"
              label="Outstanding mortgage principal"
              value={formatAmount(computed.box2)}
            />

            {/* Box 3 */}
            <FormBox
              number="3"
              label="Mortgage origination date"
              value={computed.box3 || '--'}
            />

            {/* Box 4 */}
            <FormBox
              number="4"
              label="Refund of overpaid interest"
              value={formatAmount(computed.box4)}
            />

            {/* Box 5 */}
            <FormBox
              number="5"
              label="Mortgage insurance premiums"
              value={formatAmount(computed.box5)}
            />

            {/* Box 6 */}
            <FormBox
              number="6"
              label="Points paid on purchase of principal residence"
              value={formatAmount(computed.box6)}
            />

            {/* Box 7 — checkbox */}
            <div className="flex border-b border-black">
              <div className="w-6 p-1 text-center border-r border-black text-[9px] font-bold">7</div>
              <div className="flex-1 p-1.5 flex items-center gap-1.5">
                <span className={`inline-flex items-center justify-center w-3.5 h-3.5 border border-black text-[9px] ${computed.box7 ? 'font-bold' : ''}`}>
                  {computed.box7 ? 'X' : ''}
                </span>
                <span className="text-[9px]">
                  If address of property securing mortgage is same as payer&apos;s/borrower&apos;s address, check this box
                </span>
              </div>
            </div>

            {/* Box 8 */}
            <div className="flex border-b border-black">
              <div className="w-6 p-1 text-center border-r border-black text-[9px] font-bold">8</div>
              <div className="flex-1 p-1.5">
                <div className="text-[8px] text-gray-500 uppercase mb-0.5">
                  Address or description of property securing mortgage
                </div>
                <div className="text-[10px]">{computed.box8 || '--'}</div>
              </div>
            </div>

            {/* Box 9 */}
            <FormBox
              number="9"
              label="Number of mortgaged properties"
              value="1"
            />

            {/* Box 10 */}
            <FormBox
              number="10"
              label="Other"
              value=""
            />

            {/* Box 11 */}
            <FormBox
              number="11"
              label="Mortgage acquisition date"
              value={computed.box11 || '--'}
            />
          </div>
        </div>

        {/* ─── Footer ─── */}
        <div className="border-t-2 border-black px-2 py-1 flex justify-between text-[8px]">
          <span>Form <span className="font-bold">1098</span> (Rev. 4-2025)</span>
          <span>Department of the Treasury — Internal Revenue Service</span>
        </div>
      </div>

      {/* Disclaimer */}
      <p className="text-xs text-muted-foreground text-center italic max-w-[700px] mx-auto">
        This preview covers the full interim servicing period. For multi-year loans, verify amounts against year-end statements before filing.
      </p>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Sub-components
// ---------------------------------------------------------------------------

function FormBox({
  number,
  label,
  value
}: {
  number: string;
  label: string;
  value: string;
}) {
  return (
    <div className="flex border-b border-black">
      <div className="w-6 p-1 text-center border-r border-black text-[9px] font-bold">
        {number}
      </div>
      <div className="flex-1 p-1.5">
        <div className="text-[8px] text-gray-500 uppercase mb-0.5">{label}</div>
        <div className="text-[10px] font-bold">{value}</div>
      </div>
    </div>
  );
}
